import { OfferMediaDisplayComponent } from '@/app/components/offer-media-display/offer-media-display.component';
import { SkeletonLoadingComponent } from '@/app/components/skeleton-loading/skeleton-loading.component';
import { Category } from '@/model/category';
import { CompanyUser } from '@/model/company';
import { OfferType } from '@/model/offer-type';
import { GraphqlService } from '@/service/graphql.service';
import { OfferService } from '@/service/offer.service';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Feedback } from '../../../model/feedback';
import { OfferWithImage } from '../../../model/offer-with-image';
import { FeedbackDetailsComponent } from '@/app/components/feedback-details/feedback-details.component';
import { KeycloakService } from 'keycloak-angular';
import { getAdminInstantApprovalTemplate } from '@/utils/feedback-valid-template';

@Component({
  selector: 'app-feedback',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    CommonModule,
    MatStepperModule,
    MatRadioModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatBottomSheetModule,
    MatIconModule,
    OfferMediaDisplayComponent,
    MatListModule,
    SkeletonLoadingComponent,
    DatePipe,
    FeedbackDetailsComponent,
  ],
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss'],
  standalone: true,
})
export class FeedbackComponent implements OnInit {
  private _bottomSheet = inject(MatBottomSheet);
  @ViewChild('stepper') stepper!: MatStepper;

  allCategories: Category[] = [];
  offerTypes: OfferType[] = [];
  selectedOfferType: OfferType | null = null;

  offer!: OfferWithImage;
  company!: CompanyUser;
  feedbackForm: FormGroup;
  isLinear = true;

  userIsAdmin = false;

  loadingStates = {
    offer: true,
    company: true,
    submitting: false,
  };

  constructor(
    private offerService: OfferService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private graphqlService: GraphqlService,
    private fb: FormBuilder,
    private router: Router,
    private keycloakService: KeycloakService,
  ) {
    this.feedbackForm = this.fb.group({
      appropriateContent: this.fb.group({
        isAppropriate: [null, Validators.required],
        reason: [''],
      }),
      category: this.fb.group({
        isCorrect: [null, Validators.required],
        newCategories: [[]],
        reason: [''],
      }),
      comprehensibleContent: this.fb.group({
        isComprehensible: [null, Validators.required],
        reason: [''],
      }),
      type: this.fb.group({
        isCorrect: [null, Validators.required],
        newType: [''],
        reason: [''],
      }),
      link: this.fb.group({
        isCorrect: [true, Validators.required],
        reason: [''],
      }),
      image: this.fb.group({
        isAppropriate: [null, Validators.required],
        reason: [''],
      }),
      finalDecision: [null, Validators.required],
      rejectionReason: [''],
    });

    // Add validators based on form control values
    this.feedbackForm
      .get('appropriateContent.isAppropriate')
      ?.valueChanges.subscribe((value) => {
        const reasonControl = this.feedbackForm.get(
          'appropriateContent.reason',
        );
        if (!value) {
          reasonControl?.setValidators([Validators.required]);
          // Set final decision to reject automatically when content is inappropriate
          this.feedbackForm.patchValue({
            finalDecision: 'reject',
            rejectionReason: this.feedbackForm.get('appropriateContent.reason')
              ?.value,
          });
        } else {
          reasonControl?.clearValidators();
        }
        reasonControl?.updateValueAndValidity();
      });

    // Similar validators for other form controls
    ['category', 'comprehensibleContent', 'type', 'link', 'image'].forEach(
      (controlName) => {
        let isCorrectField = 'isCorrect';
        if (controlName === 'image') {
          isCorrectField = 'isAppropriate';
        } else if (controlName === 'comprehensibleContent') {
          isCorrectField = 'isComprehensible';
        }

        this.feedbackForm
          .get(`${controlName}.${isCorrectField}`)!
          .valueChanges.subscribe((value) => {
            const reasonControl = this.feedbackForm.get(
              `${controlName}.reason`,
            );

            // Special handling for category
            if (controlName === 'category') {
              const newCategoriesControl = this.feedbackForm.get(
                'category.newCategories',
              );
              if (!value) {
                newCategoriesControl?.setValidators([
                  Validators.required,
                  (control) => {
                    const value = control.value;
                    return Array.isArray(value) && value.length > 0
                      ? null
                      : { required: true };
                  },
                ]);
              } else {
                newCategoriesControl?.clearValidators();
              }
              newCategoriesControl?.updateValueAndValidity();
            }

            if (!value) {
              reasonControl?.setValidators([Validators.required]);
            } else {
              reasonControl?.clearValidators();
            }
            reasonControl?.updateValueAndValidity();
          });
      },
    );

    // Add validator for rejection reason when final decision is 'reject'
    this.feedbackForm.get('finalDecision')?.valueChanges.subscribe((value) => {
      const rejectionReasonControl = this.feedbackForm.get('rejectionReason');
      if (value === 'reject') {
        rejectionReasonControl?.setValidators([Validators.required]);
      } else {
        rejectionReasonControl?.clearValidators();
      }
      rejectionReasonControl?.updateValueAndValidity();
    });

    this.feedbackForm.get('type.newType')?.valueChanges.subscribe((value) => {
      this.selectedOfferType =
        this.offerTypes.find((type) => type.id === value) || null;
    });
  }

  ngOnInit(): void {
    this.userIsAdmin = this.keycloakService.getUserRoles().includes('admin');
    const offerId = this.route.snapshot.paramMap.get('id');
    if (offerId) {
      this.loadOffer(offerId);
      this.loadCompanyDetails(offerId);
    }
    this.graphqlService.allCategories().subscribe((categories) => {
      this.allCategories = categories.filter(
        (category) => category.parentId === null,
      );
    });

    // Load offer types
    this.offerService.getAllOfferTypes().subscribe({
      next: (types) => {
        this.offerTypes = types;
      },
      error: (error) => {
        console.error('Error loading offer types:', error);
        this.snackBar.open('Fehler beim Laden der Angebotstypen', 'Schließen', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  loadCompanyDetails(offerId: string) {
    this.loadingStates.company = true;
    this.offerService.getCompanyDetailsByOfferId(offerId).subscribe({
      next: (company) => {
        this.company = company;
        this.loadingStates.company = false;
      },
      error: (error) => {
        console.error('Error loading company details', error);
        this.loadingStates.company = false;
      },
    });
  }

  loadOffer(id: string) {
    this.loadingStates.offer = true;
    this.offerService.getOfferById(id).subscribe(
      (offer) => {
        if (offer.status === 'REJECTED') {
          this.router.navigate(['/advisory-board']);
          this.snackBar.open(
            'Dieses Angebot wurde abgelehnt und kann nicht bearbeitet werden.',
            '',
            {
              duration: 3000,
              verticalPosition: 'bottom',
              horizontalPosition: 'center',
              panelClass: ['snackbar-error'],
            },
          );
          return;
        }
        this.offer = offer;
        this.loadingStates.offer = false;
      },
      (error) => {
        console.error('Fehler beim Laden des Angebots', error);
        this.loadingStates.offer = false;
      },
    );
  }

  submitFeedback() {
    if (this.feedbackForm.valid) {
      this.loadingStates.submitting = true;
      const feedback: Feedback = this.feedbackForm.value;

      this.offerService.submitOfferFeedback(this.offer.id, feedback).subscribe({
        next: () => {
          const message = this.getSuccessMessage(feedback.finalDecision || '');
          this.showSuccessMessage(message);
          this.router.navigate(['/advisory-board']);
        },
        error: (error: Error) => {
          console.error('Error submitting feedback:', error);
          this.snackBar.open('Fehler beim Senden des Feedbacks', '', {
            duration: 3000,
            verticalPosition: 'bottom',
            horizontalPosition: 'center',
            panelClass: ['snackbar-error'],
          });
        },
        complete: () => {
          this.offerService.clearOfferCache();
          this.loadingStates.submitting = false;
        },
      });
    }
  }

  instantApprove() {
    if (!this.userIsAdmin) {
      this.snackBar.open(
        'Nur Administratoren können Angebote sofort genehmigen.',
        '',
        {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    this.loadingStates.submitting = true;
    const feedback = getAdminInstantApprovalTemplate();

    this.offerService.submitOfferFeedback(this.offer.id, feedback).subscribe({
      next: () => {
        this.showSuccessMessage('Angebot sofort genehmigt');
        this.router.navigate(['/advisory-board']);
      },
      error: (error: Error) => {
        console.error('Error submitting feedback:', error);
        this.snackBar.open('Fehler beim Senden des Feedbacks', '', {
          duration: 3000,
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
          panelClass: ['snackbar-error'],
        });
      },
      complete: () => {
        this.offerService.clearOfferCache();
        this.loadingStates.submitting = false;
      },
    });
  }

  private getSuccessMessage(decision: string): string {
    switch (decision) {
      case 'approve':
        return 'Angebot genehmigt';
      case 'reject':
        return 'Angebot abgelehnt';
      case 'modify':
        return 'Feedback gesendet';
      default:
        return 'Feedback erfolgreich gespeichert';
    }
  }

  private showSuccessMessage(message: string) {
    this.snackBar.open(message, '', {
      duration: 3000,
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
      panelClass: ['snackbar-success'],
    });
  }

  get categoryNames(): string {
    return (
      this.offer.categories?.map((category) => category.name).join(', ') || ''
    );
  }

  openInfoAppropiate() {
    this._bottomSheet.open(InfoAppropiateComponent);
  }

  openInfoCategory() {
    this._bottomSheet.open(InfoCategoryComponent);
  }

  openInfoComprehensible() {
    this._bottomSheet.open(InfoComprehensibleComponent);
  }

  openInfoType() {
    this._bottomSheet.open(InfoTypeComponent);
  }

  openInfoLink() {
    this._bottomSheet.open(InfoLinkComponent);
  }

  openInfoImage() {
    this._bottomSheet.open(InfoImageComponent);
  }

  openInfoDecision() {
    this._bottomSheet.open(InfoDecisionComponent);
  }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  // Skip to last step and set final decision to reject
  // If the content is not appropriate we do do not gather any other feedback
  skipToLastStep() {
    if (this.stepper) {
      this.isLinear = false;
      this.feedbackForm.patchValue({
        finalDecision: 'REJECT',
        rejectionReason: this.feedbackForm.get('appropriateContent.reason')
          ?.value,
      });

      this.stepper.selectedIndex = this.stepper.steps.length - 1;
    }
  }

  getFeedbackPreview(): Feedback {
    return this.feedbackForm.value;
  }
}

@Component({
  selector: 'app-bottom-sheet-overview-example-sheet',
  templateUrl: 'info-appropiate.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoAppropiateComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoAppropiateComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-category',
  templateUrl: 'info-category.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoCategoryComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoCategoryComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-comprehensible',
  templateUrl: 'info-comprehensible.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoComprehensibleComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoComprehensibleComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-type',
  templateUrl: 'info-type.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoTypeComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoTypeComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-link',
  templateUrl: 'info-link.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoLinkComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoLinkComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-image',
  templateUrl: 'info-image.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoImageComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoImageComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}

@Component({
  selector: 'app-info-decision',
  templateUrl: 'info-decision.html',
  imports: [MatListModule],
  standalone: true,
})
export class InfoDecisionComponent {
  private _bottomSheetRef =
    inject<MatBottomSheetRef<InfoDecisionComponent>>(MatBottomSheetRef);

  openLink(event: MouseEvent): void {
    this._bottomSheetRef.dismiss();
    event.preventDefault();
  }
}
